// Form validation utilities

/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email format
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false
  }

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

/**
 * Validate UK phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid phone format
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return false
  }

  // UK phone number regex - accepts various formats
  // Allows: 01234 567890, +44 1234 567890, 07123 456789, etc.
  const phoneRegex = /^(\+44|0)\d{9,10}$/
  return phoneRegex.test(phone.trim().replace(/[\s()-]/g, ''))
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{valid: boolean, errors: string[]}} - Validation result with errors
 */
export const validatePassword = (password) => {
  const errors = []

  if (!password || typeof password !== 'string') {
    errors.push('Password is required')
    return { valid: false, errors }
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate that two passwords match
 * @param {string} password - First password
 * @param {string} confirmPassword - Confirmation password
 * @returns {boolean} - True if passwords match
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  return password === confirmPassword && password.length > 0
}

/**
 * Validate required field is not empty
 * @param {string} value - Value to validate
 * @returns {boolean} - True if field has content
 */
export const validateRequired = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return value !== null && value !== undefined
}

/**
 * Get password strength indicator
 * @param {string} password - Password to check
 * @returns {{strength: string, score: number}} - Strength indicator
 */
export const getPasswordStrength = (password) => {
  if (!password) {
    return { strength: 'none', score: 0 }
  }

  let score = 0

  // Length scoring
  if (password.length >= 8) score += 1
  if (password.length >= 12) score += 1
  if (password.length >= 16) score += 1

  // Character variety scoring
  if (/[a-z]/.test(password)) score += 1
  if (/[A-Z]/.test(password)) score += 1
  if (/[0-9]/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1

  // Map score to strength
  if (score <= 2) return { strength: 'weak', score }
  if (score <= 4) return { strength: 'medium', score }
  if (score <= 6) return { strength: 'strong', score }
  return { strength: 'very-strong', score }
}
